/**
 * SectorBenchmarkView (P168) -- the bank's posture vs the sector
 * baseline (idea #5 slice 1) + the signed contribution export (the 5b
 * federation handshake). Exec-gated server-side.
 */
import { useTranslation } from "react-i18next";
import { useState } from "react";

import { MarsadIcon } from "shared/components/MarsadIcon";
import type { BenchmarkMetric } from "shared/api/types";
import { useToast } from "shared/toast/ToastProvider";
import { useBenchmark } from "shared/benchmark/useBenchmark";
import { api } from "shared/api/client";
import { useMutation } from "@tanstack/react-query";
import { useMe } from "shared/api/useMe";
import { useDownloadContribution } from "shared/benchmark/useDownloadContribution";

const VERDICT_PILL: Record<string, string> = {
  ahead: "bg-rag-greenBg text-rag-green",
  on_par: "bg-slate-100 text-slate-600",
  behind: "bg-rag-redBg text-rag-red",
};

function MetricGauge({ m }: { m: BenchmarkMetric }) {
  const { t } = useTranslation();
  // Bar fills relative to max(value, baseline) so both markers are visible.
  const value = m.value ?? 0;
  const scale = Math.max(value, m.baseline, 1);
  const valuePct = Math.min(100, (value / scale) * 100);
  const baselinePct = Math.min(100, (m.baseline / scale) * 100);
  return (
    <div
      data-testid={`benchmark-metric-${m.id}`}
      className="rounded-lg border border-slate-200 bg-white p-4"
    >
      <div className="flex items-baseline justify-between">
        <span className="text-sm font-medium text-slate-800">{m.label}</span>
        <span
          data-testid={`benchmark-verdict-${m.id}`}
          className={`rounded px-2 py-0.5 text-[11px] font-medium ${
            VERDICT_PILL[m.verdict] ?? "bg-slate-100 text-slate-600"
          }`}
        >
          {t(`benchmark.verdict.${m.verdict}`)}
        </span>
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className="text-2xl font-semibold tabular-nums text-slate-900">
          {m.value ?? "--"}
        </span>
        <span className="text-xs text-slate-400">{m.unit}</span>
        <span className="ms-auto text-[11px] text-slate-500">
          {t("benchmark.baseline", { value: m.baseline, unit: m.unit })}
        </span>
      </div>
      {/* Round 17 B14 -- why there is no verdict, or whose rows the
          figure counts when they are not the register. */}
      {m.population && (
        <p
          data-testid={`benchmark-population-${m.id}`}
          className="mt-1 text-[11px] text-slate-500"
        >
          {m.value === null
            ? t("benchmark.population", { why: m.population })
            : t("benchmark.populationShown", { why: m.population })}
        </p>
      )}
      {/* Relative bar: brass = my value, slate tick = baseline. */}
      <div className="relative mt-2 h-1.5 rounded-full bg-slate-100">
        <div
          className="h-1.5 rounded-full bg-brand-brass"
          style={{ width: `${valuePct}%` }}
        />
        <div
          data-testid={`benchmark-baseline-tick-${m.id}`}
          className="absolute top-[-2px] h-[10px] w-[2px] bg-slate-500"
          style={{ insetInlineStart: `${baselinePct}%` }}
        />
      </div>
    </div>
  );
}

export function SectorBenchmarkView(): JSX.Element {
  const { t } = useTranslation();
  const bench = useBenchmark();
  const toast = useToast();
  const download = useDownloadContribution();
  const me = useMe();
  const [confirming, setConfirming] = useState(false);
  const isAdmin = Boolean(me.data?.is_admin);
  const destination = bench.data?.destination ?? null;
  const submit = useMutation({
    mutationFn: () =>
      api.post<{ accepted: boolean; verified: boolean | null }>(
        "/v1/benchmark/submit",
        {},
      ),
    onSuccess: () => {
      setConfirming(false);
      toast.push({ tone: "success", message: t("benchmark.submitted") });
    },
    onError: (e: { status?: number; detail?: string }) =>
      toast.push({
        tone: e.status === 503 ? "warning" : "error",
        message: e.detail ?? t("benchmark.submitError"),
      }),
  });

  function onDownload() {
    download.mutate(undefined, {
      onSuccess: () =>
        toast.push({ tone: "success", message: t("benchmark.downloaded") }),
      onError: (e) =>
        toast.push({
          tone: e.status === 403 ? "warning" : "error",
          message: e.detail ?? t("benchmark.downloadError"),
        }),
    });
  }

  return (
    <section data-testid="benchmark-view" className="flex flex-col gap-4">
      <header className="flex items-start justify-between gap-4">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-slate-400">
            {t("benchmark.kicker")}
          </p>
          <h1 className="font-display text-2xl text-slate-900">
            <MarsadIcon
              name="benchmark"
              size={20}
              className="me-2 inline-block align-[-0.1em] text-brand-brassDark"
            />
            {t("benchmark.heading")}
          </h1>
          <p className="mt-1 max-w-3xl text-sm text-slate-600">
            {t("benchmark.intro")}
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          {/* Round 10 A4 -- nothing leaves the bank off an unlabelled
              button. The control is SHOWN to every reader and enabled
              only for the role that may authorise it: hiding it taught
              nobody who could, and "who is entitled to send this" is a
              question an administrator has to answer about a control
              he can see. */}
          <button
            type="button"
            data-testid="benchmark-submit"
            onClick={() => setConfirming(true)}
            disabled={!isAdmin || submit.isPending || !destination}
            title={
              !isAdmin
                ? t("benchmark.disclose.roleLocked")
                : !destination
                  ? t("benchmark.disclose.noNetwork")
                  : undefined
            }
            className="rounded bg-brand-brass px-3 py-1.5 text-sm font-medium text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
          >
            {submit.isPending ? t("benchmark.submitting") : t("benchmark.submit")}
          </button>
          <button
            type="button"
            data-testid="benchmark-download"
            onClick={onDownload}
            disabled={download.isPending}
            className="shrink-0 rounded border border-brand-brass/40 bg-brand-brass/5 px-3 py-1.5 text-sm font-medium text-brand-brassDark hover:bg-brand-brass/10 disabled:opacity-50"
          >
            {t("benchmark.downloadContribution")}
          </button>
        </div>
      </header>

      {/* A4 -- what leaves the building, stated before the control that
          sends it. The metric list is DERIVED from the report the page
          is already rendering, because the contribution carries exactly
          those four figures: a hand-written list here could drift into
          describing a payload the product no longer sends, which is
          worse than no description at all. */}
      {bench.data && (
        <section
          data-testid="benchmark-disclosure"
          className="rounded border border-amber-200 bg-amber-50/60 p-3 text-sm"
        >
          <h2 className="font-mono text-[11px] uppercase tracking-[0.16em] text-amber-900">
            {t("benchmark.disclose.heading")}
          </h2>
          <p className="mt-1 text-slate-700">{t("benchmark.disclose.body")}</p>
          <ul
            data-testid="benchmark-disclosure-metrics"
            className="mt-2 list-inside list-disc text-slate-700"
          >
            {bench.data.metrics.map((m) => (
              <li key={m.id}>
                {m.label} ({m.unit})
              </li>
            ))}
          </ul>
          <dl className="mt-2 grid grid-cols-[auto,1fr] gap-x-3 gap-y-0.5 text-[13px]">
            <dt className="text-slate-500">
              {t("benchmark.disclose.granularityLabel")}
            </dt>
            <dd className="text-slate-800">{t("benchmark.disclose.granularity")}</dd>
            <dt className="text-slate-500">{t("benchmark.disclose.formLabel")}</dt>
            <dd className="text-slate-800">{t("benchmark.disclose.form")}</dd>
            <dt className="text-slate-500">{t("benchmark.disclose.recipientLabel")}</dt>
            <dd data-testid="benchmark-disclosure-recipient" className="text-slate-800">
              {destination ?? t("benchmark.disclose.noNetwork")}
            </dd>
            <dt className="text-slate-500">{t("benchmark.disclose.authorityLabel")}</dt>
            <dd className="text-slate-800">{t("benchmark.disclose.authority")}</dd>
          </dl>
        </section>
      )}

      {confirming && (
        <section
          data-testid="benchmark-confirm"
          className="rounded border border-rag-redLine bg-rag-redBg p-3 text-sm"
        >
          {/* The confirm RESTATES it rather than asking "are you sure?".
              A dialog that only asks for a second click adds a step and
              no information. */}
          <p className="text-slate-800">
            {t("benchmark.disclose.confirmBody", {
              count: bench.data?.metrics.length ?? 0,
              destination: destination ?? "",
            })}
          </p>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              data-testid="benchmark-confirm-send"
              onClick={() => submit.mutate()}
              disabled={submit.isPending}
              className="rounded bg-brand-brass px-3 py-1 text-sm font-medium text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
            >
              {t("benchmark.disclose.confirmSend")}
            </button>
            <button
              type="button"
              data-testid="benchmark-confirm-cancel"
              onClick={() => setConfirming(false)}
              className="rounded border border-slate-300 px-3 py-1 text-sm text-slate-700"
            >
              {t("common.cancel")}
            </button>
          </div>
        </section>
      )}

      {bench.isLoading && (
        <p data-testid="benchmark-loading" className="text-sm text-slate-500">
          {t("benchmark.loading")}
        </p>
      )}
      {bench.isError && (
        <p data-testid="benchmark-error" className="text-sm text-rag-red">
          {bench.error.status === 403 ? t("benchmark.forbidden") : t("benchmark.error")}
        </p>
      )}

      {bench.data && (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            {bench.data.metrics.map((m) => (
              <MetricGauge key={m.id} m={m} />
            ))}
          </div>
          {bench.data.peer_source === "aggregator" ? (
            <div
              data-testid="benchmark-peers"
              className="space-y-1 rounded-xl border border-paper-2 bg-white p-3 shadow-card"
            >
              <p className="font-mono text-[11px] uppercase tracking-wide">
                <span className="rounded bg-emerald-100 px-1.5 py-0.5 font-semibold text-emerald-800">
                  {t("benchmark.livePeers", {
                    n: bench.data.peer_contributors ?? "?",
                  })}
                </span>
              </p>
              <ul className="space-y-0.5 text-sm">
                {bench.data.metrics.map((m) => {
                  const peer = bench.data!.peers[m.id];
                  return (
                    <li key={m.id} className="flex flex-wrap gap-x-3">
                      <span className="w-52 text-slate-600">{m.label}</span>
                      {peer && !peer.suppressed ? (
                        <span className="font-mono text-xs text-slate-700 tabular-nums">
                          {t("screens.sectorBenchmarkView.p25")} {peer.p25}{" "}
                          {t("screens.sectorBenchmarkView.p50")} {peer.p50}{" "}
                          {t("screens.sectorBenchmarkView.p75")} {peer.p75}{" "}
                          {t("screens.sectorBenchmarkView.n")}
                          {peer.n}) — {t("benchmark.you")} {m.value ?? "--"}
                        </span>
                      ) : (
                        <span className="font-mono text-xs text-slate-400">
                          {t("benchmark.suppressed", { n: peer?.n ?? 0 })}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : (
            <p
              data-testid="benchmark-synthetic-chip"
              className="font-mono text-[11px] uppercase tracking-wide"
            >
              <span className="rounded bg-amber-100 px-1.5 py-0.5 font-semibold text-amber-800">
                {t("benchmark.syntheticChip")}
              </span>
            </p>
          )}
          <p className="text-[11px] italic text-slate-400">
            {t("benchmark.baselineSource", {
              source: bench.data.baseline_source,
            })}
          </p>
        </>
      )}
    </section>
  );
}
